import { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { Button, Spin, Alert, Space, Modal } from "antd";
import { DialogRef } from "@/types";
import { CloseOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";

interface PdfViewerRef {
  reload: () => void;
  openInNewTab: () => void;
}

interface PdfViewerProps {
  url: string;
  token?: string;
  height?: number;
  ref: React.RefObject<PdfViewerRef>;
}

async function downloadPdf(url: string, token?: string) {
  try {
    const res = await fetch(url, {
      method: "GET",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Accept: "application/pdf",
      },
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `HTTP ${res.status}`);
    }

    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("application/pdf")) {
      console.warn("Unexpected content-type:", ct);
    }

    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch (e: any) {
    throw new Error(e?.message || String(e));
  }
}

function usePdf(url: string, token?: string) {
  return useQuery({
    queryKey: [`pdf-${url}-${token}`],
    queryFn: () => downloadPdf(url, token),
    enabled: url != null && token != null,
  });
}

const PdfViewer = forwardRef<PdfViewerRef, PdfViewerProps>((props, ref) => {
  const { url, token, height = 700 } = props;

  const {
    data: blobUrl,
    refetch,
    isFetching: loading,
    isError: error,
  } = usePdf(url, token);

  useImperativeHandle(
    ref,
    () => ({
      reload: refetch,
      openInNewTab: () => {
        if (blobUrl) {
          window.open(blobUrl, "_blank");
        }
      },
    }),
    [blobUrl, refetch],
  );

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      {error && (
        <Alert
          type="error"
          showIcon
          message="Failed to load PDF"
          description={error}
        />
      )}

      <div
        style={{
          width: "100%",
          height,
          border: "1px solid #eee",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        {loading && (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin />
          </div>
        )}

        {!loading && blobUrl && (
          <iframe
            title="PDF Preview"
            src={blobUrl}
            style={{ width: "100%", height: "100%", border: "0" }}
          />
        )}

        {!loading && !blobUrl && !error && (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
            }}
          >
            No PDF loaded
          </div>
        )}
      </div>
    </Space>
  );
});

PdfViewer.displayName = "PdfViewer";

interface PdfViewerModalProps {
  url?: string;
  token?: string;
  height?: number;
}

const PdfViewerModal = forwardRef<DialogRef, PdfViewerModalProps>(
  (props, ref) => {
    const { url, token, height = 700 } = props;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const pdfViewerRef = useRef<PdfViewerRef>(null);

    const [_url, setUrl] = useState<string | undefined>(url);

    useImperativeHandle(ref, () => ({
      showModal: (props: unknown) => {
        const { url } = props as { url?: string };
        if (url != null) {
          setUrl(url);
        }
        setIsModalOpen(true);
        setTimeout(() => {
          pdfViewerRef.current?.reload();
        }, 0);
      },
      hideModal: () => setIsModalOpen(false),
    }));

    return (
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        closeIcon={null}
        width={800}
        title={
          <div className="flex flex-row items-center justify-between">
            <div className="text-lg font-bold">PDF Viewer</div>
            <div className="flex flex-row gap-2 justify-end mt-2">
              <Button onClick={() => pdfViewerRef.current?.reload()}>
                Reload
              </Button>
              <Button onClick={() => pdfViewerRef.current?.openInNewTab()}>
                Open in new tab
              </Button>
              <Button
                onClick={() => setIsModalOpen(false)}
                danger
                icon={<CloseOutlined />}
              />
            </div>
          </div>
        }
        footer={null}
      >
        <PdfViewer
          ref={pdfViewerRef}
          url={_url!}
          token={token}
          height={height}
        />
      </Modal>
    );
  },
);

PdfViewerModal.displayName = "PdfViewerModal";

export default PdfViewerModal;
